/**
 * BattlefieldUI - The player's battlefield display component.
 *
 * Responsibilities:
 * - Render up to GameConfig.maxBoardSize ordered minions from the player's battlefield
 * - Display each minion's stats (HP, attack, level) and abilities
 * - Maintain correct left-to-right ordering from the backend
 * - React to backend state updates (GameStateUpdate, etc.)
 * - Provide reactive render data for UI binding
 * - Indicate full/empty battlefield state
 *
 * The BattlefieldUI reads state from AppShell (which holds the authoritative
 * GameStateService) and computes display data for the battlefield overlay.
 * The backend is authoritative; the board state is always what the server says.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import { MinionOnBoardData } from '../../shared/messages/MessageContracts';
import { GameConfig } from '../../config/gameConfig';
import { AbilityRegistry, AbilityTriggerType } from '../../shared/models/AbilityRegistry';
import { AbilityDisplayService, AbilityDisplayData, MinionAbilityDisplayData } from '../services/AbilityDisplayService';
import { resolveAbilityNames, type AbilityDefinitionSummary } from '../../shared/models/UpgradePath';
import { GAME_ABILITY_DEFINITIONS } from '../../services/GameAbilities';

const ABILITY_SUMMARIES: AbilityDefinitionSummary[] = GAME_ABILITY_DEFINITIONS.map(
  (def) => ({ id: def.id, name: def.name, description: def.description, types: def.types, availableAtLevel: def.availableAtLevel })
);

// ─── Battlefield Minion Render Data ─────────────────────────────────────────

/**
 * Render data for a single minion on the battlefield.
 */
export interface BattlefieldMinionRenderData {
  /** Unique instance ID for this minion copy. */
  instanceId: string;
  /** The base minion definition ID. */
  baseMinionId: string;
  /** Current HP of the minion. */
  currentHp: number;
  /** Attack value of the minion. */
  attack: number;
  /** The minion's type classification (e.g., "warrior", "mage"). */
  minionType: string;
  /** Number of upgrades applied (0 = base). */
  upgradeCount: number;
  /** Computed level: upgradeCount + 1. */
  level: number;
  /** Ability IDs present on this minion. */
  abilities: string[];
  /** Human-readable ability names (resolved from shared definitions). */
  abilityNames: string[];
  /** Short descriptions of each ability (matched by index to abilityNames). */
  abilityDescriptions: string[];
  /** Whether the minion has any abilities. */
  hasAbilities: boolean;
  /** Position on the battlefield (0-indexed, left to right). */
  battlefieldPosition: number;
  /** Current upgrade path state identifier. */
  upgradePathState: string;
}

/**
 * Render data for the entire battlefield.
 */
export interface BattlefieldUIRenderData {
  /** Whether the battlefield UI should be visible (player is in a match). */
  isVisible: boolean;

  /** Array of minions on the battlefield, ordered left-to-right by battlefieldPosition. */
  minions: BattlefieldMinionRenderData[];

  /** Number of minions currently on the battlefield. */
  battlefieldSize: number;

  /** Whether the battlefield is at maximum capacity (8 minions). */
  isFull: boolean;

  /** Maximum battlefield size (from game config). */
  maxBattlefieldSize: number;

  /** Whether the battlefield is empty. */
  isEmpty: boolean;
}

/**
 * Callback invoked when the render data changes.
 */
export type BattlefieldUIRenderCallback = (data: BattlefieldUIRenderData) => void;

/**
 * Callback invoked when a minion is selected (for interaction tasks).
 */
export type BattlefieldMinionSelectedCallback = (minion: BattlefieldMinionRenderData) => void;

/**
 * Render data for a minion inspection, including full ability display details.
 */
export interface InspectedMinionData extends BattlefieldMinionRenderData {
  /** Full ability display data (name, description, trigger type for each ability). */
  abilityDisplay: MinionAbilityDisplayData;

  /** Human-readable summary of abilities for tooltip/brief display. */
  abilitySummary: string;

  /** Abilities grouped by trigger type. */
  abilitiesByTrigger: Map<AbilityTriggerType, AbilityDisplayData[]>;
}

// ─── Constants ──────────────────────────────────────────────────────────────

/** Maximum battlefield size — read from the shared game configuration so the
 *  UI always matches the authoritative server/bot capacity. */
const MAX_BATTLEFIELD_SIZE = GameConfig.maxBoardSize;

// ─── Battlefield UI ─────────────────────────────────────────────────────────

export class BattlefieldUI {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: BattlefieldUIRenderCallback[] = [];
  private _minionSelectedCallbacks: BattlefieldMinionSelectedCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  private _abilityDisplayService: AbilityDisplayService;

  constructor(appShell: AppShell, abilityRegistry?: AbilityRegistry) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;

    // Use provided registry or create a default one
    const registry = abilityRegistry ?? new AbilityRegistry();
    this._abilityDisplayService = new AbilityDisplayService(registry);
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data (computed fresh from AppShell state). */
  get renderData(): BattlefieldUIRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the battlefield UI is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the battlefield UI and start listening for state changes.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    // Listen for app shell events to trigger render updates
    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    // Initial render
    this._notifyRenderCallbacks();
  }

  /**
   * Hide the battlefield UI and stop listening for state changes.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: BattlefieldUIRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to minion selection events.
   * Returns an unsubscribe function.
   */
  onMinionSelected(callback: BattlefieldMinionSelectedCallback): () => void {
    this._minionSelectedCallbacks.push(callback);
    return () => {
      const index = this._minionSelectedCallbacks.indexOf(callback);
      if (index !== -1) {
        this._minionSelectedCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Player Actions ──────────────────────────────────────────────────────

  /**
   * Inspect a minion by its instance ID to get full ability display data.
   *
   * Resolves each ability ID on the minion using the AbilityRegistry to produce
   * human-readable name, description, and trigger type. Unknown abilities are
   * represented with a fallback display showing the raw ID.
   *
   * @param instanceId - The instance ID of the minion to inspect.
   * @returns Inspected minion data with full ability details, or null if not found.
   */
  inspectMinion(instanceId: string): InspectedMinionData | null {
    const renderData = this._buildRenderData();
    const minion = renderData.minions.find((m) => m.instanceId === instanceId) ?? null;
    if (!minion) {
      return null;
    }

    const abilityDisplay = this._abilityDisplayService.resolveAbilities(minion.abilities);
    const abilitySummary = this._abilityDisplayService.getAbilitySummary(minion.abilities);
    const abilitiesByTrigger = this._abilityDisplayService.groupByTriggerType(minion.abilities);

    return {
      ...minion,
      abilityDisplay,
      abilitySummary,
      abilitiesByTrigger,
    };
  }

  /**
   * Select a minion by its instance ID.
   * Notifies minion-selected callbacks with the minion data.
   * Returns the minion data if found, null otherwise.
   */
  selectMinion(instanceId: string): BattlefieldMinionRenderData | null {
    const renderData = this._buildRenderData();
    const minion = renderData.minions.find((m) => m.instanceId === instanceId) ?? null;
    if (minion) {
      this._notifyMinionSelectedCallbacks(minion);
    }
    return minion;
  }

  /**
   * Get a minion by its battlefield position.
   * Returns the minion data if found, null otherwise.
   */
  getMinionAtPosition(position: number): BattlefieldMinionRenderData | null {
    const renderData = this._buildRenderData();
    const minion = renderData.minions.find((m) => m.battlefieldPosition === position) ?? null;
    return minion;
  }

  /**
   * Reposition minions on the battlefield by submitting a new order.
   *
   * Validates that the new order is a valid permutation of the current
   * battlefield minions (same count, same IDs, no duplicates), then sends
   * a RepositionMinionsRequest to the backend through AppShell.
   * The backend is authoritative — it validates the reposition and produces
   * the resulting state. The UI reconciles when the GameStateUpdate arrives.
   *
   * @param newOrder - Array of minion instance IDs in the desired left-to-right order.
   * @returns true if the reposition request was sent successfully, false otherwise.
   */
  repositionMinions(newOrder: string[]): boolean {
    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: must be in preparation phase and not locked
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    const playerPhase = currentPlayer?.phase ?? matchState.phase;
    if (playerPhase !== 'preparation' || matchState.preparationLocked) {
      return false;
    }

    const renderData = this._buildRenderData();
    const currentMinions = renderData.minions;

    // Validate: new order must have same count as current battlefield
    if (newOrder.length !== currentMinions.length) {
      return false;
    }

    // Validate: new order must be a valid permutation (same IDs, no duplicates)
    const currentIds = new Set(currentMinions.map((m) => m.instanceId));
    const newIds = new Set(newOrder);

    if (currentIds.size !== newIds.size) {
      // Duplicates in new order
      return false;
    }

    for (const id of newOrder) {
      if (!currentIds.has(id)) {
        // Foreign ID not on battlefield
        return false;
      }
    }

    // Get match ID from current state
    const matchId = this._appShell.gameState.match.matchId;
    if (!matchId) {
      return false;
    }

    // Send reposition request through AppShell
    return this._appShell.repositionMinions(matchId, newOrder);
  }

  /**
   * Sell a minion from the battlefield.
   *
   * Validates that the minion exists on the current battlefield, then sends
   * a SellMinionRequest to the backend through AppShell.
   * The backend is authoritative — it validates the sale and produces
   * the resulting state. The UI reconciles when the GameStateUpdate arrives.
   *
   * The expected sale value is the minion's level (upgradeCount + 1) gold.
   * This value is provided for UI display (e.g., "Sell for 3 gold?").
   *
   * @param minionInstanceId - The instance ID of the minion to sell.
   * @returns true if the sell request was sent successfully, false otherwise.
   */
  sellMinion(minionInstanceId: string): boolean {
    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: must be in preparation phase and not locked
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    const playerPhase = currentPlayer?.phase ?? matchState.phase;
    if (playerPhase !== 'preparation' || matchState.preparationLocked) {
      return false;
    }

    // Validate: minion must exist on current battlefield
    const renderData = this._buildRenderData();
    const minion = renderData.minions.find((m) => m.instanceId === minionInstanceId) ?? null;
    if (!minion) {
      return false;
    }

    // Get match ID from current state
    const matchId = this._appShell.gameState.match.matchId;
    if (!matchId) {
      return false;
    }

    // Send sell request through AppShell
    return this._appShell.sellMinion(matchId, minionInstanceId);
  }

  /**
   * Get the expected sale value (gold) for a minion.
   * Sale value equals the minion's level: upgradeCount + 1.
   *
   * @param minionInstanceId - The instance ID of the minion.
   * @returns The expected gold value, or null if the minion is not on the battlefield.
   */
  getSaleValue(minionInstanceId: string): number | null {
    const renderData = this._buildRenderData();
    const minion = renderData.minions.find((m) => m.instanceId === minionInstanceId) ?? null;
    if (!minion) {
      return null;
    }
    return minion.level; // level = upgradeCount + 1
  }

  /**
   * Upgrade a minion on the battlefield by choosing an upgrade branch.
   *
   * Validates that the minion exists on the current battlefield, then sends
   * an UpgradeMinionRequest to the backend through AppShell.
   * The backend is authoritative — it validates the upgrade (gold cost, valid
   * branch, max upgrades not exceeded, preparation phase) and produces
   * the resulting state. The UI reconciles when the GameStateUpdate arrives.
   *
   * @param minionInstanceId - The instance ID of the minion to upgrade.
   * @param branchId - The ID of the upgrade branch to apply (e.g., "hp", "atk").
   * @returns true if the upgrade request was sent successfully, false otherwise.
   */
  upgradeMinion(minionInstanceId: string, branchId: string): boolean {
    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: minion must exist on current battlefield
    const renderData = this._buildRenderData();
    const minion = renderData.minions.find((m) => m.instanceId === minionInstanceId) ?? null;
    if (!minion) {
      return false;
    }

    // Get match ID from current state
    const matchId = this._appShell.gameState.match.matchId;
    if (!matchId) {
      return false;
    }

    // Send upgrade request through AppShell
    return this._appShell.upgradeMinion(matchId, minionInstanceId, branchId);
  }

  // ─── Internal Methods ────────────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        // Update render data on any game state change
        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        // Hide when navigating away from match screen
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyMinionSelectedCallbacks(minion: BattlefieldMinionRenderData): void {
    for (const callback of this._minionSelectedCallbacks) {
      callback(minion);
    }
  }

  private _buildRenderData(): BattlefieldUIRenderData {
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;

    // Find current player's data
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;

    // Extract battlefield minions from the authoritative backend state
    const boardMinions: MinionOnBoardData[] = currentPlayer?.battlefield ?? [];

    // Sort by battlefieldPosition to ensure correct left-to-right order
    const sortedMinions = [...boardMinions].sort(
      (a, b) => a.battlefieldPosition - b.battlefieldPosition
    );

    // Transform into render data
    const minions: BattlefieldMinionRenderData[] = sortedMinions.map((minion) => {
      const names = resolveAbilityNames(minion.abilities, ABILITY_SUMMARIES);
      const descs = minion.abilities.map((id) => {
        const def = ABILITY_SUMMARIES.find((d) => d.id === id);
        return def ? def.description : '';
      });
      return {
        instanceId: minion.instanceId,
        baseMinionId: minion.baseMinionId,
        currentHp: minion.currentHp,
        attack: minion.attack,
        minionType: minion.minionType,
        upgradeCount: minion.upgradeCount,
        level: minion.upgradeCount + 1,
        abilities: [...minion.abilities],
        abilityNames: names,
        abilityDescriptions: descs,
        hasAbilities: minion.abilities.length > 0,
        battlefieldPosition: minion.battlefieldPosition,
        upgradePathState: minion.upgradePathState,
      };
    });

    const battlefieldSize = minions.length;
    const isFull = battlefieldSize >= MAX_BATTLEFIELD_SIZE;
    const isEmpty = battlefieldSize === 0;

    // Battlefield is visible when in a match
    const isVisible = this._appShell.isInMatch;

    return {
      isVisible,
      minions,
      battlefieldSize,
      isFull,
      maxBattlefieldSize: MAX_BATTLEFIELD_SIZE,
      isEmpty,
    };
  }
}
